// src/pages/pet/PetForm.js
// Componente de formulario para crear/editar mascotas (reutilizable en modal)

import React, { useState, useEffect } from 'react';
import { Form, Row, Col, Card, Button, Spinner, Modal, Table, Alert, Badge } from 'react-bootstrap';
import { getCustomers } from '../../services/customerApi';
import { storage } from '../../firebase/firebaseConfig';
import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';

const PetForm = ({ onSubmit, loading = false, initialData = null, submitText = "Registrar Mascota" }) => {
  // Estados del formulario
  const [formData, setFormData] = useState({
    id_client: initialData?.id_client || '',
    name: initialData?.name || '',
    id_specie: initialData?.id_specie || 1,
    breed: initialData?.breed || '',
    weight_kg: initialData?.weight_kg || '',
    birthdate: initialData?.birthdate || '',
    notes: initialData?.notes || ''
  });

  // Estados para la imagen de perfil
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(initialData?.profile_image_url || '');
  const [uploadingProfileImage, setUploadingProfileImage] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState(initialData?.profile_image_url || '');
  const [uploadError, setUploadError] = useState('');

  // Estados para el modal de selección de cliente
  const [showClientModal, setShowClientModal] = useState(false);
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [clientLoading, setClientLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  // Opciones de especies (puedes expandir esto según tu backend)
  const species = [
    { id: 1, name: 'Perro' },
    { id: 2, name: 'Gato' },
    { id: 3, name: 'Ave' },
    { id: 4, name: 'Conejo' },
    { id: 5, name: 'Hamster' },
    { id: 6, name: 'Otro' }
  ];

  // Cargar clientes cuando se abre el modal
  useEffect(() => {
    if (showClientModal) {
      loadClients();
    }
  }, [showClientModal]);

  // Cargar clientes
  const loadClients = async () => {
    try {
      setClientLoading(true);
      const { ok, data } = await getCustomers({ page: 1, per_page: 50, q: clientSearch });
      
      if (ok && data?.code === 200) {
        const responseData = data.data || data.message || [];
        setClients(responseData.customers || responseData);
      }
    } catch (error) {
      console.error('Error cargando clientes:', error);
    } finally {
      setClientLoading(false);
    }
  };

  // Manejar cambios en el formulario
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Abrir modal de selección de cliente
  const handleOpenClientModal = () => {
    setShowClientModal(true);
    setClientSearch('');
  };

  // Cerrar modal de selección de cliente
  const handleCloseClientModal = () => {
    setShowClientModal(false);
    setClientSearch('');
  };

  // Seleccionar cliente
  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setFormData(prev => ({
      ...prev,
      id_client: client.id_client
    }));
    setShowClientModal(false);
  };

  // Buscar clientes
  const handleClientSearch = (e) => {
    setClientSearch(e.target.value);
    // Debounce search
    setTimeout(() => {
      loadClients();
    }, 500);
  };

  // Validar formulario
  const validateForm = () => {
    if (!formData.id_client) return 'Debes seleccionar un cliente';
    if (!formData.name) return 'El nombre de la mascota es obligatorio';
    if (!formData.id_specie) return 'Debes seleccionar una especie';
    if (!formData.weight_kg) return 'El peso es obligatorio';
    if (!formData.birthdate) return 'La fecha de nacimiento es obligatoria';
    
    // Validar peso
    const weight = parseFloat(formData.weight_kg);
    if (isNaN(weight) || weight <= 0) return 'El peso debe ser un número válido mayor a 0';
    
    // Validar fecha
    const birthdate = new Date(formData.birthdate);
    const today = new Date();
    if (birthdate > today) return 'La fecha de nacimiento no puede ser futura';
    
    return null;
  };

  // Manejar selección de imagen de perfil
  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImage(file);
      // Crear preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      // Subir inmediatamente a Firebase
      uploadImageToFirebase(file);
    }
  };

  // Subir imagen a Firebase Storage
  const uploadImageToFirebase = (file) => {
    setUploadingProfileImage(true);
    setUploadError('');

    const newFileName = `pet_profile_${Date.now()}.${file.name.split('.').pop()}`;
    const storageRef = ref(storage, `pets/${newFileName}`);
    const metadata = {
      contentType: file.type,
    };

    const uploadTask = uploadBytesResumable(storageRef, file, metadata);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        console.log(`Upload profile image is ${progress}% done`);
      },
      (error) => {
        console.error('Error uploading image:', error);
        setUploadError(`Error al subir imagen de perfil: ${error.message}`);
        setUploadingProfileImage(false);
      },
      () => {
        getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
          console.log('Profile image available at:', downloadURL);
          setProfileImageUrl(downloadURL);
          setUploadingProfileImage(false);
        });
      }
    );
  };

  // Construir payload para el backend
  const buildPayload = () => {
    const payload = {
      id_client: parseInt(formData.id_client),
      name: formData.name,
      id_specie: parseInt(formData.id_specie),
      breed: formData.breed,
      weight_kg: parseFloat(formData.weight_kg),
      birthdate: formData.birthdate,
      notes: formData.notes
    };

    // Agregar URL de imagen de perfil si existe
    if (profileImageUrl) {
      payload.profile_image_url = profileImageUrl;
    }

    return payload;
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar que si hay una imagen en proceso de subida, se espere a que termine
    if (uploadingProfileImage) {
      alert('Por favor espera a que termine de subirse la imagen de perfil');
      return;
    }
    
    const validation = validateForm();
    if (validation) {
      alert(validation); // En un caso real, usarías un sistema de notificaciones
      return;
    }

    // Si hay un error de subida pero el usuario quiere continuar, permitirlo
    // La imagen es opcional, así que se puede enviar sin ella
    const payload = buildPayload();
    await onSubmit(payload);
  };

  return (
    <>
      <Form onSubmit={handleSubmit}>
        {/* Información de la Mascota */}
        <Card className="mb-4">
          <Card.Header>
            <h6 className="mb-0">Información de la Mascota</h6>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Cliente Dueño *</Form.Label>
                  <div className="d-flex">
                    <Form.Control
                      type="text"
                      value={selectedClient ? `${selectedClient.name} ${selectedClient.surname}` : ''}
                      placeholder="Selecciona un cliente"
                      readOnly
                      className="me-2"
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={handleOpenClientModal}
                      type="button"
                    >
                      <i className="fas fa-search"></i>
                    </Button>
                  </div>
                  {formData.id_client && (
                    <Form.Text className="text-muted">
                      ID Cliente: {formData.id_client}
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre de la Mascota *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Ej: Popi"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Especie *</Form.Label>
                  <Form.Select
                    name="id_specie"
                    value={formData.id_specie}
                    onChange={handleChange}
                  >
                    {species.map(specie => (
                      <option key={specie.id} value={specie.id}>
                        {specie.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Raza</Form.Label>
                  <Form.Control
                    type="text"
                    name="breed"
                    value={formData.breed}
                    onChange={handleChange}
                    placeholder="Ej: Poodle"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Peso (kg) *</Form.Label>
                  <Form.Control
                    type="number"
                    name="weight_kg"
                    value={formData.weight_kg}
                    onChange={handleChange}
                    placeholder="3.5"
                    step="0.1"
                    min="0.1"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Fecha de Nacimiento *</Form.Label>
                  <Form.Control
                    type="date"
                    name="birthdate"
                    value={formData.birthdate}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Notas</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    placeholder="Ej: Alergia al pollo"
                    rows={3}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Sección de Imagen de Perfil */}
        <Card className="mb-4">
          <Card.Header>
            <h6 className="mb-0">
              <i className="fas fa-image me-2"></i>
              Imagen de Perfil
            </h6>
          </Card.Header>
          <Card.Body>
            {uploadError && (
              <Alert variant="danger" dismissible onClose={() => setUploadError('')}>
                {uploadError}
              </Alert>
            )}
            
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <i className="fas fa-camera me-2"></i>
                    Foto de Perfil de la Mascota (Opcional)
                  </Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={handleProfileImageChange}
                    disabled={uploadingProfileImage}
                  />
                  {uploadingProfileImage && (
                    <div className="mt-2">
                      <Spinner animation="border" size="sm" className="me-2" />
                      <small>Subiendo imagen...</small>
                    </div>
                  )}
                  {profileImagePreview && (
                    <div className="mt-3">
                      <img
                        src={profileImagePreview}
                        alt="Perfil de la mascota"
                        className="img-thumbnail"
                        style={{ maxWidth: '300px', maxHeight: '300px', objectFit: 'cover', borderRadius: '8px' }}
                      />
                      {profileImageUrl && (
                        <div className="mt-2">
                          <Badge bg="success">
                            <i className="fas fa-check me-1"></i>
                            Imagen guardada
                          </Badge>
                        </div>
                      )}
                    </div>
                  )}
                  <Form.Text className="text-muted">
                    La imagen es opcional. Si seleccionas una, se subirá automáticamente a Firebase. 
                    Solo se enviará la URL de Firebase al servidor, no el archivo.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <div className="d-grid">
          <Button
            variant="primary"
            type="submit"
            disabled={loading || uploadingProfileImage}
            size="lg"
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Procesando...
              </>
            ) : uploadingProfileImage ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Subiendo imagen...
              </>
            ) : (
              submitText
            )}
          </Button>
        </div>
      </Form>

      {/* Modal de selección de cliente */}
      <Modal show={showClientModal} onHide={handleCloseClientModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Seleccionar Cliente</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <Form.Control
              type="text"
              placeholder="Buscar cliente por nombre, apellido, email o documento..."
              value={clientSearch}
              onChange={handleClientSearch}
            />
          </div>
          
          {clientLoading ? (
            <div className="d-flex justify-content-center py-4">
              <Spinner animation="border" />
            </div>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nombre Completo</th>
                  <th>Email</th>
                  <th>WhatsApp</th>
                  <th>Documento</th>
                  <th>Acción</th>
                </tr>
              </thead>
              <tbody>
                {clients.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      {clientSearch ? 'No se encontraron clientes con ese criterio' : 'No hay clientes disponibles'}
                    </td>
                  </tr>
                ) : (
                  clients.map((client) => (
                    <tr key={client.id_client}>
                      <td>{client.id_client}</td>
                      <td>{client.name || ''} {client.surname || ''}</td>
                      <td>{client.email || 'N/A'}</td>
                      <td>{client.phone_whatsapp || 'N/A'}</td>
                      <td>{client.document || 'N/A'}</td>
                      <td>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleSelectClient(client)}
                        >
                          Seleccionar
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseClientModal}>
            Cancelar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PetForm;
